import { useMemo, useState } from 'react';
import {
  CYCLE_STATES,
  DAY_FIELDS,
  WELLBEING_LABELS,
  WELLBEING_MAX,
  WELLBEING_MIN,
  cycleLabel,
  dayOptionLabel,
  dayOptionTone,
  type ScreenContent,
} from '@lma/shared';
import type { DayEntry, DayOfWeek } from './api';
import { LegalNote, PrimaryButton, SecondaryButton, TertiaryButton } from './components';

/**
 * База дня и её история.
 *
 * Второй слой продукта. Первый отвечает на вопрос «что стало с лицом
 * после практики», второй — «каким вообще был этот день». По отдельности
 * каждый отвечает на половину: отёк спал на три пункта — это много или
 * просто выспалась?
 *
 * Отсюда же главное требование Елены: женщина должна видеть не последнюю
 * неделю, а свой календарь за месяцы. В клубе она приходит с ним, и
 * разговор идёт не «мне кажется», а «вот эти три недели».
 */

// ─── DAY Заполнение ────────────────────────────────────────

const EMPTY: DayEntry = {
  sleep: null,
  movement: null,
  food: null,
  tension: null,
  recovery: null,
  wellbeing: null,
  cycleState: null,
  cycleDay: null,
  note: null,
};

export function DayForm({
  screen,
  today,
  entry,
  busy,
  onSave,
  onBack,
}: {
  screen: ScreenContent;
  today: string;
  entry: DayEntry | null;
  busy: boolean;
  onSave: (entry: DayEntry) => void;
  onBack: () => void;
}) {
  // Экран открывается заполненным: женщина дописывает свой день,
  // а не заводит его заново каждый раз.
  const [draft, setDraft] = useState<DayEntry>(entry ?? EMPTY);
  const [showCycle, setShowCycle] = useState(
    entry?.cycleState !== null && entry?.cycleState !== undefined,
  );
  const [showNote, setShowNote] = useState(Boolean(entry?.note));

  const set = <K extends keyof DayEntry>(key: K, value: DayEntry[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <main className="screen">
      <div className="stack">
        <span className="overline">{humanDate(today)}</span>
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}

        <div className="day-form">
          {DAY_FIELDS.map((field) => (
            <fieldset className="day-field" key={field.key}>
              <legend>{field.label}</legend>
              <div className="day-options">
                {field.options.map((option) => {
                  const picked = draft[field.key as keyof DayEntry] === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`day-option${picked ? ' picked' : ''}`}
                      data-tone={option.tone}
                      aria-pressed={picked}
                      onClick={() =>
                        // Повторное нажатие снимает ответ: женщина могла
                        // промахнуться, и стирать день ради этого незачем.
                        set(field.key as keyof DayEntry, (picked ? null : option.value) as never)
                      }
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}

          <fieldset className="day-field">
            <legend>Самочувствие</legend>
            <div className="day-scale">
              {range(WELLBEING_MIN, WELLBEING_MAX).map((n) => {
                const picked = draft.wellbeing === n;
                return (
                  <button
                    key={n}
                    type="button"
                    className={`day-mark-btn${picked ? ' picked' : ''}`}
                    aria-pressed={picked}
                    aria-label={`${n} — ${WELLBEING_LABELS[n]}`}
                    onClick={() => set('wellbeing', picked ? null : n)}
                  >
                    <span className="day-mark-num">{n}</span>
                    <span className="day-mark-label">{WELLBEING_LABELS[n]}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/*
            Цикл и заметка — под кнопками, а не седьмым и восьмым пунктом.
            База дня должна занимать двадцать секунд; всё, что нужно не
            каждый день, не должно мозолить глаза каждый день.
          */}
          {showCycle ? (
            <fieldset className="day-field">
              <legend>Цикл</legend>
              <div className="day-options">
                {CYCLE_STATES.map((state) => {
                  const picked = draft.cycleState === state.value;
                  return (
                    <button
                      key={state.value}
                      type="button"
                      className={`day-option${picked ? ' picked' : ''}`}
                      aria-pressed={picked}
                      onClick={() => {
                        set('cycleState', picked ? null : state.value);
                        if (state.value !== 'day') set('cycleDay', null);
                      }}
                    >
                      {state.label}
                    </button>
                  );
                })}
              </div>

              {draft.cycleState === 'day' ? (
                <label className="ask-field cycle">
                  <span>Какой день</span>
                  <input
                    inputMode="numeric"
                    value={draft.cycleDay === null ? '' : String(draft.cycleDay)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                      set('cycleDay', digits === '' ? null : Number(digits));
                    }}
                    placeholder="14"
                  />
                </label>
              ) : null}
            </fieldset>
          ) : (
            <button type="button" className="day-extra" onClick={() => setShowCycle(true)}>
              {screen.buttons.add_cycle ?? '+ отметить цикл'}
            </button>
          )}

          {showNote ? (
            <label className="ask-field">
              <span>{screen.buttons.note_label ?? 'Своими словами'}</span>
              <textarea
                rows={3}
                maxLength={2000}
                value={draft.note ?? ''}
                onChange={(e) => set('note', e.target.value || null)}
                placeholder={
                  screen.buttons.note_hint ?? 'Например: ела много белка, десять тысяч шагов'
                }
              />
            </label>
          ) : (
            <button type="button" className="day-extra" onClick={() => setShowNote(true)}>
              {screen.buttons.add_note ?? '+ добавить заметку'}
            </button>
          )}
        </div>

        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={() => onSave(draft)} disabled={busy}>
          {busy ? 'Сохраняем…' : (screen.buttons.save ?? 'Сохранить день')}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── DAY-LOG Неделя и календарь ────────────────────────────

export function DayLog({
  screen,
  week,
  calendar,
  month,
  earliest,
  onMonth,
  onToday,
  onBack,
}: {
  screen: ScreenContent;
  week: DayOfWeek[];
  calendar: (DayEntry & { date: string })[];
  /** Первое число показываемого месяца, YYYY-MM-01. */
  month: string;
  earliest: string | null;
  onMonth: (month: string) => void;
  onToday: () => void;
  onBack: () => void;
}) {
  const [view, setView] = useState<'week' | 'month'>('week');
  const [openDay, setOpenDay] = useState<string | null>(null);

  const byDate = useMemo(
    () => new Map(calendar.map((d) => [d.date, d])),
    [calendar],
  );

  const weekByDate = useMemo(() => new Map(week.map((d) => [d.date, d])), [week]);
  const picked = openDay ? (byDate.get(openDay) ?? weekByDate.get(openDay) ?? null) : null;

  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>

        <div className="day-tabs">
          <button
            className={`chip-tab${view === 'week' ? ' active' : ''}`}
            onClick={() => setView('week')}
          >
            {screen.buttons.tab_week ?? 'Неделя'}
          </button>
          <button
            className={`chip-tab${view === 'month' ? ' active' : ''}`}
            onClick={() => setView('month')}
          >
            {screen.buttons.tab_month ?? 'Календарь'}
          </button>
        </div>

        {view === 'week' ? (
          <WeekStrip week={week} onPick={setOpenDay} />
        ) : (
          <MonthGrid
            month={month}
            byDate={byDate}
            earliest={earliest}
            onMonth={onMonth}
            onPick={setOpenDay}
          />
        )}

        {picked ? <DayCard date={openDay!} entry={picked} /> : null}

        {/* Легенда теми же цветами, что кружки: эмодзи рядом с ними
            не совпадали ни по одному оттенку. */}
        <p className="tone-legend">
          <span>
            <span className="tone-swatch" data-tone="good" aria-hidden="true" />
            спокойно
          </span>
          <span>
            <span className="tone-swatch" data-tone="mid" aria-hidden="true" />
            средне
          </span>
          <span>
            <span className="tone-swatch" data-tone="bad" aria-hidden="true" />
            тяжело
          </span>
        </p>
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={onToday}>
          {screen.buttons.today ?? 'Отметить сегодня'}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{screen.buttons.back ?? 'Назад'}</TertiaryButton>
      </div>
    </main>
  );
}

/**
 * Недельная лента.
 *
 * Строка на показатель, кружок на день. Цвет — только подсказка глазу:
 * у каждого кружка есть подпись для чтения с экрана, потому что по цвету
 * одному читать нельзя.
 */
function WeekStrip({ week, onPick }: { week: DayOfWeek[]; onPick: (date: string) => void }) {
  return (
    <div className="week-strip">
      <div className="week-head">
        <span />
        {week.map((d) => (
          <span key={d.date} className="week-day-label">
            {d.weekday}
          </span>
        ))}
      </div>

      {DAY_FIELDS.map((field) => (
        <div className="week-row" key={field.key}>
          <span className="week-row-label">{field.label}</span>
          {week.map((d) => {
            const value = d[field.key as keyof DayOfWeek] as string | null;
            const tone = dayOptionTone(field.key, value);
            const label = dayOptionLabel(field.key, value);

            return (
              <button
                key={d.date}
                className="week-dot"
                data-tone={tone ?? 'none'}
                onClick={() => onPick(d.date)}
                aria-label={`${d.weekday}, ${field.label}: ${label ?? 'не отмечено'}`}
                title={label ?? 'не отмечено'}
              />
            );
          })}
        </div>
      ))}

      <div className="week-row">
        <span className="week-row-label">Самочувствие</span>
        {week.map((d) => (
          <button
            key={d.date}
            className="week-num"
            onClick={() => onPick(d.date)}
            aria-label={`${d.weekday}, самочувствие: ${d.wellbeing ?? 'не отмечено'}`}
          >
            {d.wellbeing ?? '·'}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Месяц.
 *
 * То, ради чего всё затевалось: женщина приходит через три месяца клуба
 * и видит не последнюю неделю, а всю картину. День красится по худшей
 * из своих отметок — не оценка, а способ увидеть, где было тяжело,
 * не вчитываясь в тридцать клеток.
 */
function MonthGrid({
  month,
  byDate,
  earliest,
  onMonth,
  onPick,
}: {
  month: string;
  byDate: Map<string, DayEntry & { date: string }>;
  earliest: string | null;
  onMonth: (month: string) => void;
  onPick: (date: string) => void;
}) {
  const first = new Date(`${month}T00:00:00Z`);
  const year = first.getUTCFullYear();
  const mon = first.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(year, mon + 1, 0)).getUTCDate();

  // Понедельник первым: так принято здесь, и так неделя читается целиком.
  const shift = (new Date(Date.UTC(year, mon, 1)).getUTCDay() + 6) % 7;

  const prev = shiftMonth(month, -1);
  const next = shiftMonth(month, 1);
  const today = new Date().toISOString().slice(0, 10);

  // Листать в пустоту незачем: дальше первой записи смотреть не на что.
  const canPrev = earliest === null || prev >= earliest.slice(0, 7) + '-01';
  const canNext = next <= today.slice(0, 7) + '-01';

  return (
    <div className="month">
      <div className="month-head">
        <button
          className="month-nav"
          onClick={() => onMonth(prev)}
          disabled={!canPrev}
          aria-label="Предыдущий месяц"
        >
          ‹
        </button>
        <span className="month-title">{monthName(month)}</span>
        <button
          className="month-nav"
          onClick={() => onMonth(next)}
          disabled={!canNext}
          aria-label="Следующий месяц"
        >
          ›
        </button>
      </div>

      <div className="month-grid">
        {['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'].map((d) => (
          <span className="month-weekday" key={d}>
            {d}
          </span>
        ))}

        {Array.from({ length: shift }, (_, i) => (
          <span key={`pad-${i}`} />
        ))}

        {Array.from({ length: daysInMonth }, (_, i) => {
          const date = `${month.slice(0, 8)}${String(i + 1).padStart(2, '0')}`;
          const entry = byDate.get(date);
          const tone = entry ? worstTone(entry) : null;

          return (
            <button
              key={date}
              className={`month-day${entry ? ' filled' : ''}${date === today ? ' today' : ''}`}
              data-tone={tone ?? 'none'}
              onClick={() => onPick(date)}
              aria-label={`${i + 1} ${monthName(month)}: ${entry ? 'отмечено' : 'не отмечено'}`}
            >
              <span className="month-day-num">{i + 1}</span>
              {entry?.wellbeing ? (
                <span className="month-day-well">{entry.wellbeing}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Развёрнутый день: всё, что женщина отметила, словами. */
function DayCard({ date, entry }: { date: string; entry: DayEntry }) {
  const rows = DAY_FIELDS.map((field) => ({
    label: field.label,
    value: dayOptionLabel(field.key, (entry[field.key as keyof DayEntry] as string | null) ?? null),
  })).filter((r) => r.value);

  const cycle = cycleLabel(entry.cycleState, entry.cycleDay);
  const empty = rows.length === 0 && entry.wellbeing === null && !cycle && !entry.note;

  return (
    <section className="day-card">
      <span className="day-card-date">{humanDate(date)}</span>

      {empty ? (
        <p className="hint-line">В этот день ничего не отмечено</p>
      ) : (
        <dl className="day-card-list">
          {rows.map((r) => (
            <div key={r.label}>
              <dt>{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
          {entry.wellbeing !== null ? (
            <div>
              <dt>Самочувствие</dt>
              <dd>
                {entry.wellbeing}/5 · {WELLBEING_LABELS[entry.wellbeing]}
              </dd>
            </div>
          ) : null}
          {cycle ? (
            <div>
              <dt>Цикл</dt>
              <dd>{cycle}</dd>
            </div>
          ) : null}
        </dl>
      )}

      {entry.note ? <p className="day-card-note">{entry.note}</p> : null}
    </section>
  );
}

// ─── Мелочи ────────────────────────────────────────────────

/** Худшая отметка дня. Не приговор, а способ увидеть тяжёлые дни разом. */
function worstTone(entry: DayEntry): 'good' | 'mid' | 'bad' | null {
  let worst: 'good' | 'mid' | 'bad' | null = null;

  for (const field of DAY_FIELDS) {
    const tone = dayOptionTone(field.key, (entry[field.key as keyof DayEntry] as string) ?? null);
    if (tone === 'bad') return 'bad';
    if (tone === 'mid') worst = 'mid';
    else if (tone === 'good' && worst === null) worst = 'good';
  }

  return worst;
}

function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

function shiftMonth(month: string, by: number): string {
  const d = new Date(`${month}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + by);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

function monthName(month: string): string {
  return new Date(`${month}T00:00:00Z`).toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function humanDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
    timeZone: 'UTC',
  });
}

export { SecondaryButton };
